import { Box, FormControl, FormLabel, IconButton, InputAdornment, TextField, TextFieldProps, Typography } from '@mui/material'
import { TypographyVariant } from '@mui/material/styles/createTypography'
import { visuallyHidden } from '@mui/utils'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { Eye, EyeOff } from 'lucide-react'
import { FC, useState } from 'react'
import { UseFormRegister } from 'react-hook-form/dist/types/form'

export type C1LabeledTextFieldProps = {
    register?: UseFormRegister<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    name: string
    hideLabel?: boolean
} & TextFieldProps

const C1PasswordField: FC<Omit<C1LabeledTextFieldProps, 'type'>> = ({
    ...props
}) => {
    const [showPassword, setShowPassword] = useState(false)
    return <BaseLabeledTextField
        {...props}
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton
                        aria-label={
                            showPassword ? 'hide password' : 'show password'
                        }
                        onClick={(): void => setShowPassword(!showPassword)}
                        edge="end"
                    >
                        {showPassword ? <C1Icon icon={EyeOff} /> : <C1Icon icon={Eye} />}
                    </IconButton>
                </InputAdornment>
            ),
        }}
        type={showPassword ? 'text' : 'password'}
    />
}

export const C1LabeledTextField = ({
    ...props
}: C1LabeledTextFieldProps): React.ReactElement => {
    if (props.type === 'password') {
        return <C1PasswordField {...props} />
    }
    return <BaseLabeledTextField {...props} />
}

const BaseLabeledTextField = ({
    register,
    name,
    label,
    hideLabel,
    ...props
}: C1LabeledTextFieldProps): React.ReactElement => {
    return <FormControl fullWidth={props.fullWidth} error={props.error} disabled={props.disabled}>
        {label && <C1FormLabel label={label} htmlFor={name} style={hideLabel ? visuallyHidden : undefined} />}
        <TextField
            {...props}
            id={name}
            {...register?.(name)}
            size="small"
        />
    </FormControl>
}

export const C1LabeledTextFieldWithEditToggle: FC<C1LabeledTextFieldProps & {
    emptyText?: React.ReactElement,
    editMode: boolean,
    value: string
    typographyVariant?: TypographyVariant
}> = ({
    editMode,
    label,
    value,
    emptyText,
    typographyVariant,
    ...rest
}) => (editMode
    ? <C1LabeledTextField label={label} {...rest} />
    : <Box>
        <FormLabel>{label}</FormLabel>
        {value
            ? <Typography variant={typographyVariant}>{value}</Typography>
            : <Typography><C1EmptyIndicator message={emptyText} /></Typography>
        }
    </Box>)
